class CartPage extends HTMLElement {
    constructor() {
      super();
  
      this._selectors = {
        form: "[js-cart-form]",
        header: "#header-container",
        qtyInput: "[js-qty-input]",
      };
    }
  
    connectedCallback() {
      this.form = this.querySelector(this._selectors.form);
      this.header = document.querySelector(this._selectors.header);
      this.qtyInputs = this.querySelectorAll(this._selectors.qtyInput);
      document.addEventListener("QTYChange", this.handleQuantityChange.bind(this));
  
      this.qtyInputs.forEach((input) => {
        input.addEventListener("change", this.handleQuantityChange.bind(this));
      });
  
      this._setVariables();
      this._watchWindowResize();
    }
  
    _watchWindowResize = (evt) => {
      window.addEventListener("resize", this._setVariables);
    };
  
    _setVariables = (evt) => {
      document.documentElement.style.setProperty(
        "--header-height",
        `${this.header.clientHeight}px`
      );
    };
  
    handleQuantityChange(e) {
      if (this.form) {
        this.form.submit();
      }
    }
}

customElements.define("cart-page", CartPage);

const SEED_SPOT_DY_EVENTS = {
    searchEnabled: "Search Enabled",
    productClick: "Search Product Click",
    termClick: "Search Term Click",
    searchCompleted: "Search Completed"
};
const SEED_SPOT_DY_SELECTORS = {
    searchInput: ".s-spot-instant-search-input, .dropdown-search__input, .dropdown-search__form input[name='q']",
    currentSearchForm: ".dropdown-search__form",
    searchIcon: ".js-seed-open-instant-search, .js-open-instant-search-collections, [js-seed-open-instant-search], .dropdown-search__submit, .nav-icons__search",
    productResult: "[data-seed-spot-product-result], .search-dropdown .results a.product.item",
    searchTerm: "[data-seed-spot-search-term], .search-dropdown .popularSearches a",
    seeAllResults: ".search-dropdown-contents-see-all-button, .search-dropdown .show-all-results"
};
const SEED_SPOT_DY_SEARCH_ENABLED_STORAGE_KEY = "seedSpotDySearchEnabledTracked";
const SEED_SPOT_DY_NAVIGATION_DELAY_MS = 200;

function trackSeedSpotDyEvent(eventName, properties) {
    if (!window.DY || typeof window.DY.API !== "function") return;

    try {
        window.DY.API("event", {
            name: eventName,
            properties: properties || {}
        });
    } catch (err) {
        // Keep DY failures from blocking search interactions or navigation.
    }
}

function hasSeedSpotDyTracking() {
    return !!(window.DY && typeof window.DY.API === "function");
}

function closestSeedSpotElement(target, selector) {
    return target && typeof target.closest === "function" ? target.closest(selector) : null;
}

function getSeedSpotSearchTermFromElement(element) {
    const form = closestSeedSpotElement(element, ".dropdown-search__form, .seed-dropdown-search__form");
    const input = form ? form.querySelector("input[name='q']") : null;
    if (input && input.value) return input.value.trim();

    const seedSpotRoot = closestSeedSpotElement(element, "s-spot-instant-search");
    const seedSpotInput = seedSpotRoot ? seedSpotRoot.querySelector("input[name='q']") : null;
    if (seedSpotInput && seedSpotInput.value) return seedSpotInput.value.trim();

    const dropdown = closestSeedSpotElement(element, ".search-dropdown, .search-dropdown-container");
    const dropdownForm = dropdown ? closestSeedSpotElement(dropdown, "form") : null;
    const dropdownInput = dropdownForm ? dropdownForm.querySelector("input[name='q']") : null;
    if (dropdownInput && dropdownInput.value) return dropdownInput.value.trim();

    return "";
}

function getSeedSpotLinkSearchTerm(link, fallbackElement) {
    const explicitTerm = link.getAttribute("data-search-term") || link.getAttribute("data-seed-spot-search-term");
    if (explicitTerm) return explicitTerm.trim();

    try {
        const url = new URL(link.href, window.location.origin);
        const queryTerm = url.searchParams.get("q");
        if (queryTerm) return queryTerm.trim();
    } catch (err) {
        // Fall back to visible link text or the nearest search input.
    }

    const textTerm = link.textContent ? link.textContent.trim() : "";
    return textTerm || getSeedSpotSearchTermFromElement(fallbackElement || link);
}

function isSeedSpotPlainNavigationClick(event, link) {
    return !!(
        link &&
        event.button === 0 &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.shiftKey &&
        !event.altKey &&
        (!link.target || link.target === "_self")
    );
}

function navigateSeedSpotLinkAfterDyTracking(event, link, fallbackURL) {
    if (!hasSeedSpotDyTracking() || !isSeedSpotPlainNavigationClick(event, link)) return false;

    event.preventDefault();
    window.setTimeout(() => {
        window.location.href = link.href || fallbackURL;
    }, SEED_SPOT_DY_NAVIGATION_DELAY_MS);
    return true;
}

function trackSeedSpotSearchEnabled(properties) {
    if (!window.DY || typeof window.DY.API !== "function") return;
    if (window.__seedSpotSearchEnabledDyTracked) return;

    try {
        if (window.sessionStorage.getItem(SEED_SPOT_DY_SEARCH_ENABLED_STORAGE_KEY) === "true") {
            window.__seedSpotSearchEnabledDyTracked = true;
            return;
        }
    } catch (err) {
        // Fall back to the in-page flag when sessionStorage is unavailable.
    }

    window.__seedSpotSearchEnabledDyTracked = true;
    try {
        window.sessionStorage.setItem(SEED_SPOT_DY_SEARCH_ENABLED_STORAGE_KEY, "true");
    } catch (err) {
        // Fall back to the in-page flag when sessionStorage is unavailable.
    }
    trackSeedSpotDyEvent(SEED_SPOT_DY_EVENTS.searchEnabled, properties);
}

function bindSeedSpotDySearchEnabledTracking() {
    if (window.__seedSpotDySearchEnabledTrackingBound) return;
    window.__seedSpotDySearchEnabledTrackingBound = true;

    document.addEventListener("focusin", (event) => {
        if (!closestSeedSpotElement(event.target, SEED_SPOT_DY_SELECTORS.searchInput)) return;
        trackSeedSpotSearchEnabled({ interactionType: "search_field_focus" });
    });

    document.addEventListener("click", (event) => {
        if (closestSeedSpotElement(event.target, SEED_SPOT_DY_SELECTORS.searchIcon)) {
            trackSeedSpotSearchEnabled({ interactionType: "search_icon_click" });
            return;
        }

        if (closestSeedSpotElement(event.target, SEED_SPOT_DY_SELECTORS.searchInput)) {
            trackSeedSpotSearchEnabled({ interactionType: "search_field_click" });
        }
    });

    document.addEventListener("submit", (event) => {
        const form = closestSeedSpotElement(event.target, SEED_SPOT_DY_SELECTORS.currentSearchForm);
        if (!form) return;

        const input = form.querySelector("input[name='q']");
        const searchTerm = input ? input.value.trim() : "";
        if (!searchTerm.replace(/\s/g, "").length) return;

        trackSeedSpotDyEvent(SEED_SPOT_DY_EVENTS.searchCompleted, {
            searchTerm,
            sourceView: "current",
            sourcePageUrl: window.location.href
        });

        if (event.defaultPrevented) return;

        if (hasSeedSpotDyTracking()) {
            event.preventDefault();
            window.setTimeout(() => {
                form.submit();
            }, SEED_SPOT_DY_NAVIGATION_DELAY_MS);
        }
    });

    document.addEventListener("click", (event) => {
        if (closestSeedSpotElement(event.target, "s-spot-instant-search")) return;

        const termLink = closestSeedSpotElement(event.target, SEED_SPOT_DY_SELECTORS.searchTerm);
        if (termLink) {
            const searchTerm = getSeedSpotLinkSearchTerm(termLink, event.target);
            if (searchTerm) {
                trackSeedSpotDyEvent(SEED_SPOT_DY_EVENTS.termClick, {
                    searchTerm,
                    sourceView: "current",
                    sourcePageUrl: window.location.href
                });
            }
            return;
        }

        const productLink = closestSeedSpotElement(event.target, SEED_SPOT_DY_SELECTORS.productResult);
        if (productLink) {
            const searchTerm = getSeedSpotSearchTermFromElement(productLink);
            trackSeedSpotDyEvent(SEED_SPOT_DY_EVENTS.productClick, {
                searchTerm,
                sourceView: "current",
                sourcePageUrl: window.location.href,
                productName: productLink.querySelector(".title")?.textContent?.trim() || "",
                productUrl: productLink.getAttribute("href") || ""
            });
            navigateSeedSpotLinkAfterDyTracking(event, productLink, productLink.href);
            return;
        }

        const seeAllLink = closestSeedSpotElement(event.target, SEED_SPOT_DY_SELECTORS.seeAllResults);
        if (seeAllLink) {
            const searchTerm = getSeedSpotLinkSearchTerm(seeAllLink, event.target);
            if (searchTerm) {
                trackSeedSpotDyEvent(SEED_SPOT_DY_EVENTS.searchCompleted, {
                    searchTerm,
                    sourceView: "current",
                    sourcePageUrl: window.location.href
                });
            }
        }
    }, true);
}

class SeedSpotInstantSearch extends HTMLElement {
    constructor() {
        super();

        this._selectors = {
            input: 'input[name="q"]',
            form: ".seed-dropdown-search__form",
            dropdown: "[js-seed-spot-dropdown]",
            config: "[js-seed-spot-instant-settings]",
            searchDropdownContainer: "[js-search-dropdown-container]",
            searchBar: "[js-seed-spot-search-bar]",
            searchBarClose: "[js-seed-spot-search-bar-close]"
        };
        this._onDocumentClick = this.handleDocumentClick.bind(this);
        this._onDocumentKeydown = this._handleDocumentKeydown.bind(this);
        this._onSearchFormSubmit = this._handleSearchFormSubmit.bind(this);
        this._onSearchInputKeydown = this._handleSearchInputKeydown.bind(this);
        this._onSearchResultsClick = this._handleSearchResultsClick.bind(this);
        this._closeAnimating = false;
        this._searchDebounceTimer = null;
        this._spotSearchAbort = null;
        this._spotPrefs = {};
        this._bodyScrollLockY = null;
        this._exactTitleMatchURL = null;
        this._exactTitleMatchCollectionURL = null;
        this._exactTitleMatchQuery = null;
        this._redirectCollection = true;
        this._redirectPage = true;
        this._redirectProduct = false;
        this._redirectRelevantIfNoProducts = true;
    }

    connectedCallback() {
        bindSeedSpotDySearchEnabledTracking();

        this.input = this.querySelector(this._selectors.input);
        this.form = this.querySelector(this._selectors.form);
        this.searchDropdownContainer = this.querySelector(this._selectors.searchDropdownContainer);
        this.searchBarClose = this.querySelector(this._selectors.searchBarClose);
        this.view = this.getAttribute("data-view");
        const configEl = this.querySelector(this._selectors.config);
        if (configEl) {
            try {
                this._spotPrefs = JSON.parse(configEl.textContent || "{}");
            } catch (err) {
                console.error("Error parsing seed spot config:", err);
            }
        }

        if (this.form) {
            this.form.addEventListener("submit", this._onSearchFormSubmit);
        }
        if (this.input) {
            this.input.addEventListener("keydown", this._onSearchInputKeydown);
        }
        if (this.searchDropdownContainer) {
            this.searchDropdownContainer.addEventListener("click", this._onSearchResultsClick);
        }

        if (this.view == 'mobile') {
            if (this.input) {
                this.input.addEventListener("click", this.handleInputClick.bind(this));
                this.input.addEventListener("input", this._handleInputChange.bind(this));
            }
            if (this.searchBarClose) {
                this.searchBarClose.addEventListener("click", this._handleSearchBarClose.bind(this));
                this.searchBarClose.addEventListener("keydown", (e) => {
                    if (e.key !== "Enter" && e.key !== " ") return;
                    e.preventDefault();
                    this._handleSearchBarClose(e);
                });
            }
            document.removeEventListener("click", this._onDocumentClick, true);
            document.addEventListener("click", this._onDocumentClick, true);
            document.removeEventListener("keydown", this._onDocumentKeydown);
            document.addEventListener("keydown", this._onDocumentKeydown);
        } else if (this.view == 'drawer-instant-search') {
            this._initDefaultSearchDropdownContents();
            if (this.input) {
                this.input.addEventListener("input", this._handleInputChange.bind(this));
            }
        } else if (this.view == 'desktop') {
            if (this.input) {
                this.input.addEventListener("click", this._handleDrawerInputClick.bind(this));
            }
        }
    }

    disconnectedCallback() {
        if (this.form) {
            this.form.removeEventListener("submit", this._onSearchFormSubmit);
        }
        if (this.input) {
            this.input.removeEventListener("keydown", this._onSearchInputKeydown);
        }
        if (this.searchDropdownContainer) {
            this.searchDropdownContainer.removeEventListener("click", this._onSearchResultsClick);
        }
        document.removeEventListener("click", this._onDocumentClick, true);
        document.removeEventListener("keydown", this._onDocumentKeydown);
        clearTimeout(this._searchDebounceTimer);
        this._searchDebounceTimer = null;
        if (this._spotSearchAbort) {
            this._spotSearchAbort.abort();
            this._spotSearchAbort = null;
        }
        this._unlockBodyScrollForMobileSearch();
    }

    _handleDrawerInputClick(evt) {
        if (evt) {
            evt.preventDefault();
            evt.stopPropagation();
        }
        var shell = document.querySelector("drawer-instant-search");
        if (shell && typeof shell.openInstantSearchDrawer === "function") {
            shell.openInstantSearchDrawer(evt);
        }
    }

    _handleInputChange() {
        const searchValue = this.input ? this.input.value : "";
        clearTimeout(this._searchDebounceTimer);
        this._searchDebounceTimer = setTimeout(() => {
            const trimmed = (searchValue || "").trim();
            if (!trimmed) {
                this._initDefaultSearchDropdownContents();
            } else {
                this._generateSearchDropdownContents(searchValue);
            }
        }, 280);
    }

    _escapeHtml(text) {
        if (text == null || text === "") return "";
        const div = document.createElement("div");
        div.textContent = String(text);
        return div.innerHTML;
    }

    _escapeAttr(text) {
        return String(text)
            .replace(/&/g, "&amp;")
            .replace(/"/g, "&quot;")
            .replace(/</g, "&lt;");
    }

    _formatPrice(price) {
        if (price == null || price === "") return "";

        const moneyFormat = this._spotPrefs && typeof this._spotPrefs.moneyFormat === "string"
            ? this._spotPrefs.moneyFormat
            : undefined;

        if (
            window.slate &&
            window.slate.Currency &&
            typeof window.slate.Currency.formatMoney === "function"
        ) {
            return window.slate.Currency.formatMoney(price, moneyFormat);
        }

        if (window.spotDOM && typeof window.spotDOM.formatMoney === "function") {
            return window.spotDOM.formatMoney(Number(price) / 100, moneyFormat);
        }

        const numericPrice = Number(price);
        return Number.isFinite(numericPrice) ? `$${(numericPrice / 100).toFixed(2)}` : String(price);
    }

    _isMobileSpotSearchView() {
        return this.view === "mobile";
    }

    _lockBodyScrollForMobileSearch() {
        if (!document.body.classList.contains("seed-navigation")) return;
        if (!this._isMobileSpotSearchView()) return;
        if (this._bodyScrollLockY !== null) return;
        this._bodyScrollLockY = window.scrollY || document.documentElement.scrollTop || 0;
        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
        document.body.style.position = "fixed";
        document.body.style.top = `-${this._bodyScrollLockY}px`;
        document.body.style.width = "100%";
    }

    _unlockBodyScrollForMobileSearch() {
        if (this._bodyScrollLockY === null) return;
        const y = this._bodyScrollLockY;
        document.documentElement.style.overflow = "";
        document.body.style.overflow = "";
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
        window.scrollTo(0, y);
        this._bodyScrollLockY = null;
    }

    _defaultDropdownHtmlStorageKey() {
        const host =
            this._spotPrefs && typeof this._spotPrefs.hostname === "string" && this._spotPrefs.hostname
                ? this._spotPrefs.hostname
                : "default";
        return `seed-spot-instant-search-default-dropdown-html:${host}`;
    }

    _persistDefaultDropdownHtml(markup) {
        if (typeof localStorage === "undefined" || markup == null || markup === "") return;
        try {
            localStorage.setItem(this._defaultDropdownHtmlStorageKey(), markup);
        } catch (err) {
            /* quota / private mode */
        }
    }

    _getCachedDefaultDropdownHtml() {
        if (typeof localStorage === "undefined") return "";
        try {
            return localStorage.getItem(this._defaultDropdownHtmlStorageKey()) || "";
        } catch (err) {
            return "";
        }
    }

    _getSpotRequestContext() {
        const shopify = window.Shopify || {};
        const locale = document.documentElement.lang || shopify.locale || "en";
        const currencyCode = (shopify.currency && shopify.currency.active) || "USD";
        const currencyRate =
            shopify.currency && shopify.currency.rate != null
                ? String(shopify.currency.rate)
                : document.documentElement.getAttribute("data-currency-rate") || "1.0";
        const market =
            shopify.country ||
            document.documentElement.getAttribute("data-market") ||
            "US";
        return {
            locale,
            currency: { code: currencyCode, rate: currencyRate },
            market
        };
    }

    _buildSpotSearchRequestBody(trimmed, targets) {
        const { locale, currency, market } = this._getSpotRequestContext();
        return {
            query: [
                {
                    and: [
                        {
                            "variant-custom-field": {
                                "show-color-or-tag": { "==": "1" }
                            }
                        },
                        {
                            price: { ">": "0" }
                        }
                    ]
                }
            ],
            attributes: {
                locale,
                rows: 6,
                targets,
                search: {
                    query: trimmed
                },
                count: true,
                count_type: "exact",
                allVariants: false,
                autoCorrect: true,
                category: "QuickSearch",
                currency,
                market,
                split: "auto",
                pins: []
            }
        };
    }

    _buildSpotSearchBarRequestBody(search, targets, options) {
        const opts = options || {};
        const { locale, currency, market } = this._getSpotRequestContext();
        const spotDOM = window.spotDOM;
        const attributes = {
            locale,
            rows: opts.rows != null ? opts.rows : 6,
            targets,
            search: {
                query: search
            },
            allVariants: spotDOM && typeof spotDOM.allVariants === "function" ? spotDOM.allVariants() : false,
            autoCorrect: spotDOM && typeof spotDOM.autoCorrect === "function" ? spotDOM.autoCorrect() : true,
            category: "QuickSearch",
            currency,
            market,
            split: "auto",
            pins: []
        };
        if (opts.count) {
            attributes.count = true;
            attributes.count_type = "exact";
        }
        return {
            query: [],
            attributes
        };
    }

    _spotSearchFetch(url, body, signal) {
        return fetch(url, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json; charset=UTF-8"
            },
            body: JSON.stringify(body),
            signal
        }).then((res) => {
            if (!res.ok) throw new Error(`Spot HTTP ${res.status}`);
            return res.json();
        });
    }

    _normalizeSearch(search) {
        const spotDOM = window.spotDOM;
        if (spotDOM && typeof spotDOM.normalizeSearch === "function") {
            return spotDOM.normalizeSearch(search);
        }
        return String(search)
            .toLowerCase()
            .replace(/^\s+|\s+$/, "");
    }

    _getShopifyPath(pathSegment) {
        const root =
            window.Shopify && window.Shopify.routes && window.Shopify.routes.root
                ? window.Shopify.routes.root
                : "/";
        const normalizedRoot = root.endsWith("/") ? root : `${root}/`;
        const segment = String(pathSegment).replace(/^\//, "");
        return `${normalizedRoot}${segment}`;
    }

    _isCollectionURL(url) {
        if (!url || typeof url !== "string") return false;
        return /\/collections\/[^/?#]+/.test(url);
    }

    _getBoundSpotSearchBar() {
        const spotDefault = window.SpotDefault || window.spotDefault;
        if (!spotDefault || !this.input || !Array.isArray(spotDefault.spotSearchBars)) return null;
        return spotDefault.spotSearchBars.find((bar) => bar.inputElement === this.input) || null;
    }

    _getSpotLocale() {
        const spotDOM = window.spotDOM;
        if (spotDOM && typeof spotDOM.locale === "function") {
            return spotDOM.locale();
        }
        return document.documentElement.lang || (window.Shopify && window.Shopify.locale) || "en";
    }

    _getSpotSearchRedirects() {
        const spotDOM = window.spotDOM;
        if (spotDOM && typeof spotDOM.redirects === "function") {
            const redirects = spotDOM.redirects();
            if (Array.isArray(redirects) && redirects.length) {
                return redirects;
            }
        }
        const settings = this._spotPrefs && this._spotPrefs.settings;
        return settings && Array.isArray(settings.redirects) ? settings.redirects : [];
    }

    _removeDiacritics(value) {
        const spotDOM = window.spotDOM;
        if (spotDOM && typeof spotDOM.removeDiacritics === "function") {
            return spotDOM.removeDiacritics(value);
        }
        return String(value);
    }

    // Match Spot SearchBar.checkTitleMatches: exact normalized handle/title only.
    _entityMatchesSearch(entity, normalizedSearch) {
        if (!entity || !normalizedSearch) return false;
        const handle = entity.handle ? this._normalizeSearch(entity.handle) : "";
        const title = entity.title ? this._normalizeSearch(entity.title) : "";
        return handle === normalizedSearch || title === normalizedSearch;
    }

    _getSearchPageRedirectURL(search) {
        const spotDefault = window.SpotDefault || window.spotDefault;
        const spotDOM = window.spotDOM;
        const locale = this._getSpotLocale();

        // Prefer Spot's redirect engine only when redirects are actually loaded.
        // Otherwise fall back to settings.redirects from seed config JSON.
        if (spotDefault && typeof spotDefault.getSearchPageRedirect === "function" && spotDOM) {
            const spotRedirects = typeof spotDOM.redirects === "function" ? spotDOM.redirects() : [];
            if (Array.isArray(spotRedirects) && spotRedirects.length) {
                return spotDefault.getSearchPageRedirect(locale, search) || null;
            }
        }

        const redirects = this._getSpotSearchRedirects();
        if (!redirects.length) return null;

        const normalizedSearch = this._normalizeSearch(search);
        const candidate = redirects.find((entry) => {
            if (entry.locale && locale && entry.locale !== locale) return false;
            return this._removeDiacritics(String(entry.term || "").toLowerCase())
                .split(/\s*,\s*/)
                .some((term) => {
                    if (!term) return false;
                    const index = normalizedSearch.indexOf(term);
                    if (index === -1) return false;
                    return (
                        entry.type === "substring" ||
                        (entry.type === "exact" &&
                            index === 0 &&
                            normalizedSearch.length === term.length) ||
                        (entry.type === "word" &&
                            (index + term.length === normalizedSearch.length ||
                                /\W/.test(normalizedSearch.charAt(index + term.length))))
                    );
                });
        });

        if (!candidate || !candidate.url) return null;
        if (candidate.url.indexOf("//") !== -1) return candidate.url;

        let remainingSearch = search;
        this._removeDiacritics(String(candidate.term || "").toLowerCase())
            .split(/\s*,\s*/)
            .forEach((term) => {
                if (!term) return;
                const index = this._removeDiacritics(remainingSearch.toLowerCase()).indexOf(
                    this._removeDiacritics(term.toLowerCase())
                );
                if (index !== -1) {
                    remainingSearch = remainingSearch
                        .substr(0, index)
                        .replace(/\S+$/, "")
                        .concat(remainingSearch.substr(index + term.length).replace(/^\S+/, ""))
                        .replace(/\s\s/, " ")
                        .replace(/(^\s*|\s*$)/, "");
                }
            });

        let url = candidate.url;
        if (url.indexOf("/") === 0 && url.indexOf("//") === -1) {
            url = this._getShopifyPath(url.slice(1));
        }

        if (!remainingSearch) return url;
        return `${url}${url.indexOf("?") === -1 ? "?" : "&"}context=${encodeURIComponent(remainingSearch)}`;
    }

    _checkTitleMatches(search, results) {
        const searchBar = this._getBoundSpotSearchBar();
        if (searchBar && typeof searchBar.checkTitleMatches === "function") {
            const spotURL = searchBar.checkTitleMatches(search, results);
            if (spotURL) return spotURL;
        }

        let url = null;
        const normalizedSearch = this._normalizeSearch(search);
        const collections = Array.isArray(results.collections) ? results.collections : [];
        const pages = Array.isArray(results.pages) ? results.pages : [];
        const products = Array.isArray(results.products) ? results.products : [];

        if (this._redirectCollection && collections.length > 0) {
            const targets = collections.filter((collection) =>
                this._entityMatchesSearch(collection, normalizedSearch)
            );
            if (targets.length > 0 && targets[0].handle) {
                url = this._getShopifyPath(`collections/${targets[0].handle}`);
            }
        }

        if (this._redirectPage && pages.length > 0 && !url) {
            const targets = pages.filter((page) => this._entityMatchesSearch(page, normalizedSearch));
            if (targets.length > 0 && targets[0].handle) {
                url = this._getShopifyPath(`pages/${targets[0].handle}`);
            }
        }

        if (this._redirectProduct && products.length > 0 && !url) {
            const targets = products.filter((product) => this._entityMatchesSearch(product, normalizedSearch));
            if (targets.length > 0 && targets[0].handle) {
                url = this._getShopifyPath(`products/${targets[0].handle}`);
            }
        }

        // Spot falls back to the top collection/page result when there are no products.
        if (
            this._redirectRelevantIfNoProducts &&
            products.length === 0 &&
            !url &&
            (collections.length > 0 || pages.length > 0)
        ) {
            if (collections[0] && collections[0].handle) {
                url = this._getShopifyPath(`collections/${collections[0].handle}`);
            } else if (pages[0] && pages[0].handle) {
                url = this._getShopifyPath(`pages/${pages[0].handle}`);
            }
        }

        return url;
    }

    _storeSearchMatchContext(search, results) {
        this._exactTitleMatchQuery = search;
        const matchURL = this._checkTitleMatches(search, results);
        if (matchURL && this._isCollectionURL(matchURL)) {
            this._exactTitleMatchCollectionURL = matchURL;
            this._exactTitleMatchURL = null;
        } else {
            this._exactTitleMatchCollectionURL = null;
            this._exactTitleMatchURL = matchURL;
        }
    }

    _clearExactTitleMatchCache() {
        this._exactTitleMatchQuery = null;
        this._exactTitleMatchURL = null;
        this._exactTitleMatchCollectionURL = null;
    }

    _getSpotClusterUrl() {
        const clusters = this._spotPrefs.clusters;
        const hostname = this._spotPrefs.hostname;
        const clusterHost =
            Array.isArray(clusters) && typeof clusters[0] === "string" && clusters[0] ? clusters[0] : null;
        if (!clusterHost || !hostname) return null;
        return `https://${clusterHost}/?hostname=${encodeURIComponent(hostname)}`;
    }

    _fetchSearchMatchResults(search, signal) {
        const searchBar = this._getBoundSpotSearchBar();
        if (searchBar && typeof searchBar.getQuery === "function") {
            return new Promise((resolve, reject) => {
                if (signal && signal.aborted) {
                    reject(new DOMException("Aborted", "AbortError"));
                    return;
                }
                try {
                    searchBar
                        .getQuery(search)
                        .e()
                        .done((products, count, results) => {
                            if (signal && signal.aborted) {
                                reject(new DOMException("Aborted", "AbortError"));
                                return;
                            }
                            resolve(results || {});
                        })
                        .fail(reject);
                } catch (err) {
                    reject(err);
                }
            });
        }

        const url = this._getSpotClusterUrl();
        if (!url) return Promise.reject(new Error("Missing Spot cluster config"));

        const collectionBody = this._buildSpotSearchBarRequestBody(search, ["collections", "pages"], {
            rows: 50
        });
        const productBody = this._buildSpotSearchBarRequestBody(search, ["products"], { rows: 1 });

        return Promise.all([
            this._spotSearchFetch(url, collectionBody, signal),
            this._spotSearchFetch(url, productBody, signal)
        ]).then(([collectionResults, productResults]) => ({
            collections: Array.isArray(collectionResults && collectionResults.collections)
                ? collectionResults.collections
                : [],
            pages: Array.isArray(collectionResults && collectionResults.pages) ? collectionResults.pages : [],
            products: Array.isArray(productResults && productResults.products) ? productResults.products : []
        }));
    }

    _fetchSearchMatchContext(search) {
        return this._fetchSearchMatchResults(search).then((results) => {
            this._storeSearchMatchContext(search, results);
            return results;
        });
    }

    _resolveSearchSubmitTargets(search) {
        let collectionURL = this._exactTitleMatchCollectionURL;
        let exactURL = this._exactTitleMatchURL;
        const searchBar = this._getBoundSpotSearchBar();

        if (searchBar && searchBar.lastResult === search) {
            const spotMatchURL = searchBar.exactTitleMatchCollectionURL || searchBar.exactTitleMatchURL;
            if (spotMatchURL) {
                if (this._isCollectionURL(spotMatchURL)) {
                    collectionURL = spotMatchURL;
                    exactURL = null;
                } else {
                    exactURL = spotMatchURL;
                    collectionURL = null;
                }
            }
        }

        return { collectionURL, exactURL };
    }

    _getSearchPageURL(search) {
        const spotDefault = window.SpotDefault || window.spotDefault;
        if (spotDefault && typeof spotDefault.getSearchPageURL === "function") {
            return spotDefault.getSearchPageURL(search);
        }
        const redirectURL = this._getSearchPageRedirectURL(search);
        if (redirectURL) return redirectURL;
        const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : "/";
        return `${root}search?q=${encodeURIComponent(search)}`;
    }

    _assignSearchRedirect(url, search) {
        const spotDefault = window.SpotDefault || window.spotDefault;
        const spotDOM = window.spotDOM;
        const location = spotDOM && spotDOM.location ? spotDOM.location : window.location;

        if (this._isCollectionURL(url)) {
            if (spotDefault && typeof spotDefault.redirectToCollectionPage === "function") {
                spotDefault.redirectToCollectionPage(url, search);
            } else {
                location.assign(url);
            }
            return;
        }

        location.assign(url);
    }

    // Match Spot SearchBar.submit priority:
    // 1) configured redirects  2) exact title match  3) search page
    _navigateForSearchSubmit(search) {
        const spotDefault = window.SpotDefault || window.spotDefault;
        const redirectURL = this._getSearchPageRedirectURL(search);
        if (redirectURL) {
            this._assignSearchRedirect(redirectURL, search);
            return;
        }

        const { collectionURL, exactURL } = this._resolveSearchSubmitTargets(search);
        if (collectionURL) {
            this._assignSearchRedirect(collectionURL, search);
            return;
        }

        if (exactURL) {
            this._assignSearchRedirect(exactURL, search);
            return;
        }

        if (spotDefault && typeof spotDefault.redirectToSearchPage === "function") {
            spotDefault.redirectToSearchPage(search);
        } else {
            window.location.assign(this._getSearchPageURL(search));
        }
    }

    _navigateAfterDyTracking(callback) {
        if (!hasSeedSpotDyTracking()) {
            callback();
            return;
        }

        window.setTimeout(callback, SEED_SPOT_DY_NAVIGATION_DELAY_MS);
    }

    _submitSearch(search) {
        const searchTerm = search != null ? String(search).trim() : "";
        if (!searchTerm.replace(/\s/g, "").length) return Promise.resolve();
        this._trackSearchCompleted(searchTerm);

        if (
            this._exactTitleMatchQuery === searchTerm &&
            (this._exactTitleMatchCollectionURL || this._exactTitleMatchURL)
        ) {
            this._navigateAfterDyTracking(() => {
                this._navigateForSearchSubmit(searchTerm);
            });
            return Promise.resolve();
        }

        return this._fetchSearchMatchContext(searchTerm)
            .then(() => {
                this._navigateAfterDyTracking(() => {
                    this._navigateForSearchSubmit(searchTerm);
                });
            })
            .catch((err) => {
                if (err.name === "AbortError") return;
                this._navigateAfterDyTracking(() => {
                    this._navigateForSearchSubmit(searchTerm);
                });
            });
    }

    _renderPopularSearchRow(term) {
        const termEsc = this._escapeHtml(term);
        const searchHref = this._escapeAttr(this._getSearchPageURL(term));
        const termAttr = this._escapeAttr(term);
        return `<li class="collection_item" role="option">
                            <a class="search-dropdown-popular-searches-item-link" href="${searchHref}" data-search-term="${termAttr}" data-seed-spot-search-term="${termAttr}"><p class="search-dropdown-popular-searches-item">${termEsc}</p></a>
                        </li>`;
    }

    _handleSearchResultsClick(e) {
        if (!this.searchDropdownContainer) return;

        const termLink = closestSeedSpotElement(e.target, SEED_SPOT_DY_SELECTORS.searchTerm);
        if (termLink && this.searchDropdownContainer.contains(termLink)) {
            const term = termLink.getAttribute("data-search-term") || termLink.getAttribute("data-seed-spot-search-term");
            if (!term) return;

            this._trackSearchTermClick(term);
            e.preventDefault();
            clearTimeout(this._searchDebounceTimer);
            if (this.input) this.input.value = term;
            void this._submitSearch(term);
            return;
        }

        const productLink = closestSeedSpotElement(e.target, SEED_SPOT_DY_SELECTORS.productResult);
        if (productLink && this.searchDropdownContainer.contains(productLink)) {
            this._trackSearchProductClick(productLink);
            if (isSeedSpotPlainNavigationClick(e, productLink) && hasSeedSpotDyTracking()) {
                e.preventDefault();
                this._navigateAfterDyTracking(() => {
                    window.location.href = productLink.href;
                });
            }
            return;
        }

        const seeAllLink = closestSeedSpotElement(e.target, SEED_SPOT_DY_SELECTORS.seeAllResults);
        const searchTerm = this.input ? this.input.value.trim() : "";
        if (seeAllLink && this.searchDropdownContainer.contains(seeAllLink) && searchTerm.replace(/\s/g, "").length) {
            this._trackSearchCompleted(searchTerm);
            if (isSeedSpotPlainNavigationClick(e, seeAllLink) && hasSeedSpotDyTracking()) {
                e.preventDefault();
                this._navigateAfterDyTracking(() => {
                    window.location.href = seeAllLink.href;
                });
            }
        }
    }

    _handleSearchFormSubmit(e) {
        e.preventDefault();
        clearTimeout(this._searchDebounceTimer);
        void this._submitSearch(this.input ? this.input.value : "");
    }

    _handleSearchInputKeydown(e) {
        if (e.key !== "Enter") return;
        e.preventDefault();
        clearTimeout(this._searchDebounceTimer);
        void this._submitSearch(this.input ? this.input.value : "");
    }

    _getDySearchProperties(searchTerm) {
        return {
            searchTerm: searchTerm || "",
            sourceView: this.view || "",
            sourcePageUrl: window.location.href
        };
    }

    _trackSearchCompleted(searchTerm) {
        trackSeedSpotDyEvent(SEED_SPOT_DY_EVENTS.searchCompleted, this._getDySearchProperties(searchTerm));
    }

    _trackSearchTermClick(searchTerm) {
        trackSeedSpotDyEvent(SEED_SPOT_DY_EVENTS.termClick, this._getDySearchProperties(searchTerm));
    }

    _trackSearchProductClick(productLink) {
        const searchTerm = this.input ? this.input.value.trim() : "";
        trackSeedSpotDyEvent(SEED_SPOT_DY_EVENTS.productClick, Object.assign(this._getDySearchProperties(searchTerm), {
            productName: productLink.getAttribute("data-product-title") || "",
            productHandle: productLink.getAttribute("data-product-handle") || "",
            productUrl: productLink.getAttribute("href") || ""
        }));
    }

    _initDefaultSearchDropdownContents() {
        const trimmed = ("").trim();

        const clusters = this._spotPrefs.clusters;
        const hostname = this._spotPrefs.hostname;
        const clusterHost =
            Array.isArray(clusters) && typeof clusters[0] === "string" && clusters[0] ? clusters[0] : null;
        if (!clusterHost || !hostname) {
            console.warn("[s-spot-instant-search] Missing clusters or hostname in config.");
            return Promise.resolve();
        }

        if (this._spotSearchAbort) {
            this._spotSearchAbort.abort();
        }
        this._spotSearchAbort = new AbortController();
        const { signal } = this._spotSearchAbort;

        const url = `https://${clusterHost}/?hostname=${encodeURIComponent(hostname)}`;
        const { locale, currency, market } = this._getSpotRequestContext();
        const body = {
            query: [
                {
                    and: [
                        {
                            "variant-custom-field": {
                                "show-color-or-tag": { "==": "1" }
                            }
                        },
                        {
                            price: { ">": "0" }
                        }
                    ]
                }
            ],
            attributes: {
                locale,
                rows: 6,
                targets: ["products", "collections", "popularSearches"],
                search: {
                    query: trimmed
                },
                allVariants: false,
                autoCorrect: true,
                category: "QuickSearch",
                currency,
                count: true,
                count_type: "exact",
                market,
                split: "auto",
                pins: []
            }
        };

        return fetch(url, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json; charset=UTF-8"
            },
            body: JSON.stringify(body),
            signal
        })
            .then((res) => {
                if (!res.ok) throw new Error(`Spot HTTP ${res.status}`);
                return res.json();
            })
            .then((data) => {
                if (!this.searchDropdownContainer) return;

                const popularSearches = Array.isArray(data.popularSearches) ? data.popularSearches : [];
                const popularSearchesRows = [];
                for (let i = 0; i < 3; i++) {
                    const popularSearch = popularSearches[i];
                    const term = popularSearch && popularSearch.term != null ? String(popularSearch.term) : "";
                    if (!term) continue;
                    popularSearchesRows.push(this._renderPopularSearchRow(term));
                }

                const products = Array.isArray(data.products) ? data.products : [];
                const productRows = [];
                for (let i = 0; i < products.length; i++) {
                    const product = products[i];
                    const title = product.title != null ? String(product.title) : "";
                    const handle = product.handle != null ? String(product.handle) : "";
                    const image =
                        product.image && product.image.src
                            ? String(product.image.src)
                            : product.images &&
                                product.images[0] &&
                                product.images[0].src
                              ? String(product.images[0].src)
                              : "";
                    const metafields = Array.isArray(product.metafields) ? product.metafields : [];
                    const collectionBadge =
                        metafields.find((m) => m && m.key === "product_badge_in_collection")?.value || "";
                    const productBadge = metafields.find((m) => m && m.key === "product_badges")?.value || "";
                    const variant = Array.isArray(product.variants) && product.variants[0] ? product.variants[0] : null;
                    const price = variant && variant.price != null ? variant.price : "";
                    const productUrl = handle ? `/products/${encodeURIComponent(handle)}` : "#";

                    const formattedPrice = this._formatPrice(price);

                    productRows.push(`<li class="collection_item" role="option">
                        <a class="collection_item-link" href="${this._escapeAttr(productUrl)}" data-seed-spot-product-result="true" data-product-title="${this._escapeAttr(title)}" data-product-handle="${this._escapeAttr(handle)}">
                            ${collectionBadge ? `<p class="collection_item-badge">${this._escapeHtml(collectionBadge)}</p>` : ""}
                            ${image ? `<img class="collection_item-image" src="${this._escapeAttr(image)}" alt="${this._escapeAttr(title)}">` : ""}
                            ${productBadge ? `<p class="collection_item-product-badge">${this._escapeHtml(productBadge)}</p>` : ""}
                            <h3 class="collection_item-title">${this._escapeHtml(title)}</h3>
                            <p class="collection_item-price">${this._escapeHtml(formattedPrice)}</p>
                        </a>
                        </li>`);
                }

                const defaultMarkup = `
                  <div class="search-dropdown-contents search-dropdown-contents-popular">
                    ${
                        popularSearchesRows.length > 0
                            ? `
                               <h3 class="search-dropdown-contents-title">Top Searches</h3>
                               <ul class="search-dropdown-popular-searches default">${popularSearchesRows.join(
                                  ""
                              )}</ul>`
                            : ""
                    }
                    ${
                        productRows.length > 0
                            ? `<ul class="search-dropdown-contents-products">${productRows.join(
                                  ""
                              )}</ul>`
                            : ""
                    }
                  </div>
                `;

                const savedMarkUp = `
                  <div class="search-dropdown-contents search-dropdown-contents-popular">
                    ${
                        productRows.length > 0
                            ? `<ul class="search-dropdown-contents-products">${productRows.join(
                                  ""
                              )}</ul>`
                            : ""
                    }
                    <a class="search-dropdown-contents-see-all-button" href="/collections/new-arrivals">New Arrivals</a>
                  </div>
                `;
                this.searchDropdownContainer.innerHTML = defaultMarkup;
                this._persistDefaultDropdownHtml(savedMarkUp);
            })
            .catch((err) => {
                if (err.name === "AbortError") return;
                console.error("[s-spot-instant-search] fetch failed:", err);
            });
    }

    _generateSearchDropdownContents(searchValue) {
        const searchTerm = searchValue != null ? String(searchValue) : "";
        const trimmed = searchTerm.trim();
        if (!trimmed) {
            if (this._spotSearchAbort) {
                this._spotSearchAbort.abort();
                this._spotSearchAbort = null;
            }
            this._clearExactTitleMatchCache();
            if (this.searchDropdownContainer) {
                this.searchDropdownContainer.innerHTML = '<div class="search-dropdown-contents"></div>';
            }
            return Promise.resolve();
        }

        const clusters = this._spotPrefs.clusters;
        const hostname = this._spotPrefs.hostname;
        const clusterHost =
            Array.isArray(clusters) && typeof clusters[0] === "string" && clusters[0] ? clusters[0] : null;
        if (!clusterHost || !hostname) {
            console.warn("[s-spot-instant-search] Missing clusters or hostname in config.");
            return Promise.resolve();
        }

        if (this._spotSearchAbort) {
            this._spotSearchAbort.abort();
        }
        this._spotSearchAbort = new AbortController();
        const { signal } = this._spotSearchAbort;

        const url = `https://${clusterHost}/?hostname=${encodeURIComponent(hostname)}`;
        const dropdownBody = this._buildSpotSearchRequestBody(trimmed, [
            "products",
            "collections",
            "popularSearches"
        ]);
        const countBody = this._buildSpotSearchRequestBody(trimmed, ["products", "collections"]);

        return Promise.all([
            this._spotSearchFetch(url, dropdownBody, signal),
            this._spotSearchFetch(url, countBody, signal),
            this._fetchSearchMatchResults(searchTerm, signal)
        ])
            .then(([data, countData, matchData]) => {
                if (!this.searchDropdownContainer) return;
                this._storeSearchMatchContext(searchTerm, matchData);
                const resultCount = countData.count != null ? countData.count : null;
                const searchHref = `/search?q=${encodeURIComponent(trimmed)}`;
                const popularSearches = Array.isArray(data.popularSearches) ? data.popularSearches : [];
                const popularSearchesRows = [];
                for (let i = 0; i < 3; i++) {
                    const popularSearch = popularSearches[i];
                    const term = popularSearch && popularSearch.term != null ? String(popularSearch.term) : "";
                    if (!term) continue;
                    popularSearchesRows.push(this._renderPopularSearchRow(term));
                }
                const products = Array.isArray(data.products) ? data.products : [];
                const productRows = [];
                for (let i = 0; i < products.length; i++) {
                    const product = products[i];
                    const title = product.title != null ? String(product.title) : "";
                    const handle = product.handle != null ? String(product.handle) : "";
                    const image =
                        product.image && product.image.src
                            ? String(product.image.src)
                            : product.images &&
                                product.images[0] &&
                                product.images[0].src
                              ? String(product.images[0].src)
                              : "";
                    const metafields = Array.isArray(product.metafields) ? product.metafields : [];
                    const collectionBadge =
                        metafields.find((m) => m && m.key === "product_badge_in_collection")?.value || "";
                    const productBadge = metafields.find((m) => m && m.key === "product_badges")?.value || "";
                    const variant = Array.isArray(product.variants) && product.variants[0] ? product.variants[0] : null;
                    const price = variant && variant.price != null ? variant.price : "";
                    const productUrl = handle ? `/products/${encodeURIComponent(handle)}` : "#";

                    const formattedPrice = this._formatPrice(price);

                    productRows.push(`<li class="collection_item" role="option">
                        <a class="collection_item-link" href="${this._escapeAttr(productUrl)}" data-seed-spot-product-result="true" data-product-title="${this._escapeAttr(title)}" data-product-handle="${this._escapeAttr(handle)}">
                            ${collectionBadge ? `<p class="collection_item-badge">${this._escapeHtml(collectionBadge)}</p>` : ""}
                            ${image ? `<img class="collection_item-image" src="${this._escapeAttr(image)}" alt="${this._escapeAttr(title)}">` : ""}
                            ${productBadge ? `<p class="collection_item-product-badge">${this._escapeHtml(productBadge)}</p>` : ""}
                            <h3 class="collection_item-title">${this._escapeHtml(title)}</h3>
                            <p class="collection_item-price">${this._escapeHtml(formattedPrice)}</p>
                        </a>
                        </li>`);
                }

                if (productRows.length > 0) {
                    this.searchDropdownContainer.innerHTML = `
                    <div class="search-dropdown-contents">
                    ${
                        popularSearchesRows.length > 0
                            ? `
                        <h3 class="search-dropdown-contents-title">Top Searches</h3>
                        <ul class="search-dropdown-popular-searches">
                            ${popularSearchesRows.join("")}
                        </ul>
                    `
                            : ""
                    }
                    <ul class="search-dropdown-contents-products">
                        ${productRows.join("")}
                    </ul>
                    <a class="search-dropdown-contents-see-all-button" href="${this._escapeAttr(searchHref)}">
                        See All ${resultCount != null ? `(${resultCount})` : ""}
                    </a>
                    </div>
                    `;
               
                } else {
                    const cached = this._getCachedDefaultDropdownHtml();
                    if (cached) {
                        this.searchDropdownContainer.innerHTML = cached;
                        const root = this.searchDropdownContainer.querySelector(".search-dropdown-contents");
                        if (root) {
                            root.insertAdjacentHTML(
                                "afterbegin",
                                `<p class="search-dropdown-contents-no-results">We couldn't find that, try these instead.</p>`
                            );
                        }
                    } else {
                        this.searchDropdownContainer.innerHTML = `
                    <div class="search-dropdown-contents">
                        <p class="search-dropdown-contents-no-results">We couldn't find that, try these instead.</p>
                    </div>
                    `;
                    }
                }
            })
            .catch((err) => {
                if (err.name === "AbortError") return;
                console.error("[s-spot-instant-search] fetch failed:", err);
            });
    }

    _handleSearchBarClose(e) {
        e.preventDefault();
        e.stopPropagation();
        this._dismissActive();
    }

    handleDocumentClick(e) {
        if (!this.contains(e.target)) {
            this._dismissActive();
        }
    }

    _handleDocumentKeydown(e) {
        if (e.key !== "Escape") return;
        if (!this.classList.contains("is-active")) return;
        e.preventDefault();
        this._dismissActive();
    }

    _dismissActive() {
        if (this.input) this.input.value = "";
        this._clearExactTitleMatchCache();
        if (this.searchDropdownContainer) {
            this.searchDropdownContainer.innerHTML = '<div class="search-dropdown-contents"></div>';
        }

        if (!this.classList.contains("is-active")) {
            this._clearSearchFormMotion();
            return;
        }
        if (this._closeAnimating) return;
        this._runCloseFlip();
    }

    _runCloseFlip() {
        const bar = this.querySelector(this._selectors.searchBar);
        if (!bar) {
            this.classList.remove("is-active");
            this._unlockBodyScrollForMobileSearch();
            return;
        }

        this._closeAnimating = true;
        const first = bar.getBoundingClientRect();
        this.classList.remove("is-active");
        this._unlockBodyScrollForMobileSearch();

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (!bar.isConnected) {
                    this._closeAnimating = false;
                    return;
                }

                const last = bar.getBoundingClientRect();
                const dx = first.left - last.left;
                const dy = first.top - last.top;

                if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) {
                    this._clearSearchFormMotion();
                    this._closeAnimating = false;
                    return;
                }

                bar.style.transition = "none";
                bar.style.transform = `translate(${dx}px, ${dy}px)`;
                void bar.offsetHeight;
                bar.style.transition = "";
                bar.style.transform = "";

                const finish = () => {
                    this._clearSearchFormMotion();
                    this._closeAnimating = false;
                };

                const onEnd = (ev) => {
                    if (ev.target !== bar || ev.propertyName !== "transform") return;
                    bar.removeEventListener("transitionend", onEnd);
                    clearTimeout(fallbackId);
                    finish();
                };

                const fallbackId = setTimeout(() => {
                    bar.removeEventListener("transitionend", onEnd);
                    finish();
                }, 750);

                bar.addEventListener("transitionend", onEnd);
            });
        });
    }

    _clearSearchFormMotion() {
        const bar = this.querySelector(this._selectors.searchBar);
        if (!bar) return;
        bar.style.transition = "";
        bar.style.transform = "";
    }

    handleInputClick(e) {
        e.preventDefault();
        e.stopPropagation();
        if (this._closeAnimating) return;
        if (this.classList.contains("is-active")) return;
        this._initDefaultSearchDropdownContents();

        const bar = this.querySelector(this._selectors.searchBar);
        const first = bar ? bar.getBoundingClientRect() : null;

        this.classList.add("is-active");
        this._lockBodyScrollForMobileSearch();

        if (!bar || !first) return;

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const last = bar.getBoundingClientRect();
                const dx = first.left - last.left;
                const dy = first.top - last.top;
                if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return;

                bar.style.transition = "none";
                bar.style.transform = `translate(${dx}px, ${dy}px)`;
                void bar.offsetHeight;
                bar.style.transition = "";
                bar.style.transform = "";
            });
        });
    }
}

customElements.define("s-spot-instant-search", SeedSpotInstantSearch);

class DrawerInstantSearch extends HTMLElement {
    constructor() {
        super();
        this._drawerId = "seed-instant-search-drawer";
    }

    connectedCallback() {
        if (this._seedInstantSearchDrawerInit) {
            return;
        }
        var t = window.theme;
        var $ = window.jQuery;
        if (typeof $ === "undefined" || !t || typeof t.Drawers !== "function") {
            return;
        }
        if (!document.getElementById(this._drawerId)) {
            return;
        }
        var drawerId = this._drawerId;
        var instance = new t.Drawers(drawerId, "right", {
            open: ".js-seed-open-instant-search, .js-open-instant-search-collections",
            dirOpenClass: "js-drawer-open-instant-search",
            openClass: "js-drawer-open",
            onDrawerOpen: function () {
                var $drawer = $("#" + drawerId);
                var $input = $drawer
                    .find('input[name="q"]')
                    .first();
                var focusInput = function () {
                    if (!$input.length) return;
                    if (!$('html, body').hasClass("js-drawer-open-instant-search")) return;
                    $input[0].focus();
                };
                if ($input.length && window.slate && slate.a11y) {
                    slate.a11y.removeTrapFocus({
                        $container: $drawer,
                        namespace: 'drawer_focus'
                    });
                    $drawer.attr("tabindex", "-1");
                    $(document).on('focusin.drawer_focus', function (evt) {
                        if (!$('html, body').hasClass("js-drawer-open-instant-search")) return;
                        if ($drawer[0] !== evt.target && !$drawer.has(evt.target).length) {
                            focusInput();
                        }
                    });
                }
                if ($drawer.length && $input.length && $drawer.hasClass("is-transitioning")) {
                    var observer = new MutationObserver(function () {
                        if ($drawer.hasClass("is-transitioning")) return;
                        observer.disconnect();
                        focusInput();
                    });
                    observer.observe($drawer[0], { attributes: true, attributeFilter: ["class"] });
                } else if ($input.length) {
                    focusInput();
                }
            }
        });
        if (instance) {
            this._drawerInstance = instance;
            this._seedInstantSearchDrawerInit = true;
        }
    }

    /**
     * @param {Event} [evt] — pass the user click so theme.Drawers runs onDrawerOpen (focus drawer search).
     */
    openInstantSearchDrawer(evt) {
        if (!this._drawerInstance || typeof this._drawerInstance.open !== "function") {
            return;
        }
        this._drawerInstance.open(evt);
    }
}

customElements.define("drawer-instant-search", DrawerInstantSearch);

class MobileMenu extends HTMLElement {
  constructor() {
    super();

    this._selectors = {
      container: "[js-container]",
      triggers: "[js-trigger]",
      back: "[js-back]",
      titles: "[js-title]",
      parents: "[js-parent]",
      linkChildren: "[js-child]",
      linkGrandchildren: "[js-grandchild]",
      headerBack: "[js-mobile-menu-back-btn]",
    };

    this._level = 0;
    this._activeParentId = null;
  }

  connectedCallback() {
    this.container = this.querySelector(this._selectors.container);
    if (!this.container) return;

    this.triggers = this.querySelectorAll(this._selectors.triggers);
    this.backs = this.querySelectorAll(this._selectors.back);
    this.titles = this.querySelectorAll(this._selectors.titles);
    this.parents = this.querySelectorAll(this._selectors.parents);
    this.linkChildren = this.querySelectorAll(this._selectors.linkChildren);
    this.linkGrandchildren = this.querySelectorAll(
      this._selectors.linkGrandchildren
    );
    this.headerBackBtn = this.querySelector(this._selectors.headerBack);
    this.headerBackText = this.headerBackBtn?.querySelector(
      ".mobile-nav-drawer__back-text"
    );
    this.panels = this.querySelectorAll(".mobile-nav__slider-panel");

    this.linkChildren.forEach((child) => this._hide(child));
    this.linkGrandchildren.forEach((grandchild) => this._hide(grandchild));

    this._setEventListeners();
    this._watchDrawerClose();
    this._syncInactivePanels(0);
  }

  disconnectedCallback() {
    this._drawerObserver?.disconnect();
  }

  _setEventListeners() {
    this.triggers.forEach((trigger) => {
      trigger.addEventListener("click", this._slideToChild);
    });

    this.headerBackBtn?.addEventListener("click", this._headerBackOnClick);
  }

  _watchDrawerClose() {
    this._drawerWasOpen = document.body.classList.contains("js-drawer-open-left");

    this._drawerObserver = new MutationObserver(this._onDrawerClassChange);
    this._drawerObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }

  _onDrawerClassChange = () => {
    const isOpen = document.body.classList.contains("js-drawer-open-left");

    if (this._drawerWasOpen && !isOpen) {
      this._navigateToLevel(0, null, { moveFocus: false });
    }

    this._drawerWasOpen = isOpen;
  };

  _headerBackOnClick = (evt) => {
    evt.preventDefault();
    if (this._level <= 0) return;

    if (this._level === 2) {
      this._navigateToLevel(1, this._activeParentId);
      return;
    }

    this._navigateToLevel(0, null);
  };

  _slideToChild = (evt) => {
    evt.preventDefault();
    const level = Number(evt.currentTarget.getAttribute("data-level"));
    const id = evt.currentTarget.getAttribute("data-id");
    this._navigateToLevel(level, id);
  };

  _hide(el) {
    el.classList.remove("is-active");
    el.setAttribute("hidden", "");
  }

  _show(el) {
    el.classList.add("is-active");
    el.removeAttribute("hidden");
  }

  _setTitle(id) {
    if (!this.headerBackText) return;

    const title = Array.from(this.titles).find(
      (el) => el.getAttribute("data-id") === id
    );

    if (title) {
      this.headerBackText.textContent = title.textContent.trim();
    }
  }

  _navigateToLevel(level, id, { moveFocus = true } = {}) {
    const translate =
      level > 0 ? `translateX(${((-100 / 3) * level).toFixed(4)}%)` : null;

    switch (level) {
      case 1:
        this._activeParentId = id;

        this.backs.forEach((back) => {
          back.setAttribute("data-id", id);
        });

        this._setTitle(id);

        this.linkChildren.forEach((child) => {
          this._hide(child);

          if (child.getAttribute("data-id") === id) {
            this._show(child);
          }
        });

        this.parents.forEach((parent) => {
          if (parent.getAttribute("aria-hidden") === "false") {
            parent.style.display = "none";
          }
        });

        this.linkGrandchildren.forEach((grandchild) => {
          this._hide(grandchild);
        });
        break;

      case 2:
        this._setTitle(id);

        this.linkGrandchildren.forEach((grandchild) => {
          this._hide(grandchild);

          if (grandchild.getAttribute("data-id") === id) {
            this._show(grandchild);
          }
        });
        break;

      default:
        this._activeParentId = null;

        this.parents.forEach((parent) => {
          if (parent.getAttribute("aria-hidden") === "false") {
            parent.removeAttribute("style");
          }
        });

        this.linkChildren.forEach((child) => {
          this._hide(child);
        });

        this.linkGrandchildren.forEach((grandchild) => {
          this._hide(grandchild);
        });

        if (this.headerBackText) {
          this.headerBackText.textContent = "BACK";
        }
        break;
    }

    if (translate) {
      this.container.style.transform = translate;
    } else {
      this.container.removeAttribute("style");
    }

    this._level = level;
    this.dataset.active = level > 0 ? "true" : "false";

    this._resetPanelScroll(level);
    this._syncInactivePanels(level);

    if (moveFocus) {
      this._focusActivePanel(level);
    }
  };

  _syncInactivePanels(level) {
    this.panels?.forEach((panel, index) => {
      const isActive = index === level;

      if (isActive) {
        panel.removeAttribute("inert");
        panel.removeAttribute("aria-hidden");
        return;
      }

      panel.setAttribute("inert", "");
      panel.setAttribute("aria-hidden", "true");
    });
  }

  _focusActivePanel(level) {
    const panel = this.panels?.[level];
    if (!panel) return;

    const focusable = panel.querySelectorAll("a[href], button:not([disabled])");
    const target =
      Array.from(focusable).find((el) => !el.closest("[hidden]")) ||
      this.headerBackBtn;

    if (!target || typeof target.focus !== "function") return;

    target.focus({ preventScroll: true });
  }

  _resetPanelScroll(level) {
    const panel = this.panels?.[level];

    if (!panel) return;

    panel.scrollTop = 0;
  }
}

customElements.define("s-mobile-menu", MobileMenu);